// =====================================================================
// Assessment Center — Instructor Panel
// Layout shell: fixed sidebar + topbar, matching the AviationHub shell
// pattern (white canvas, navy accents, condensed nav labels).
// =====================================================================
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/auth/AuthContext";

const navItems = [
  { to: "/instructor/assessment-center", label: "Dashboard", end: true, icon: "◧" },
  { to: "/instructor/assessment-center/exams", label: "Exams", icon: "▤" },
  { to: "/instructor/assessment-center/question-bank", label: "Question Bank", icon: "▥" },
  { to: "/instructor/assessment-center/grading", label: "Grading", icon: "✓" },
];

export default function InstructorLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  function handleSignOut() {
    signOut();
    navigate("/");
  }

  return (
    <div className="min-h-screen bg-navy-50/40 font-body text-slate-800">
      <div className="mx-auto flex max-w-[1400px]">
        {/* Sidebar */}
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border-soft bg-white lg:flex">
          <div className="flex items-center gap-2 border-b border-border-soft px-5 py-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-700 font-display text-base font-bold text-white">
              AH
            </div>
            <div>
              <p className="font-display text-sm font-semibold leading-tight text-navy-900">
                AviationHub
              </p>
              <p className="text-xs leading-tight text-slate-400">Assessment Center</p>
            </div>
          </div>

          <nav className="flex-1 space-y-1 px-3 py-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-navy-700 text-white"
                      : "text-slate-600 hover:bg-navy-50 hover:text-navy-800"
                  }`
                }
              >
                <span aria-hidden className="text-base">
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-border-soft px-5 py-4">
            <p className="text-xs text-slate-400">Instructor Panel</p>
            <p className="text-xs text-slate-400">Student pages live separately.</p>
          </div>
        </aside>

        {/* Main column */}
        <div className="min-h-screen flex-1">
          {/* Topbar */}
          <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border-soft bg-white/95 px-6 py-4 backdrop-blur">
            <div className="flex items-center gap-2 lg:hidden">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy-700 font-display text-sm font-bold text-white">
                AH
              </div>
              <span className="font-display text-sm font-semibold text-navy-900">
                Assessment Center
              </span>
            </div>
            <div className="hidden lg:block" />
            <div className="flex items-center gap-3">
              <span className="hidden text-sm text-slate-500 sm:inline">{user?.fullName ?? "Instructor"}</span>
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full bg-navy-100 font-display text-sm font-semibold text-navy-700"
                aria-hidden
              >
                IN
              </div>
              <button
                onClick={handleSignOut}
                className="rounded-lg border border-border-soft px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-navy-50 hover:text-navy-700"
              >
                Sign out
              </button>
            </div>
          </header>

          {/* Mobile nav */}
          <nav className="flex gap-1 overflow-x-auto border-b border-border-soft bg-white px-4 py-2 lg:hidden">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium ${
                    isActive ? "bg-navy-700 text-white" : "text-slate-600 hover:bg-navy-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <main className="px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
